import { asIsoDate } from 'components/shared/DatePicker/utils';
import { addBusinessDays, parseISO } from 'date-fns';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { OtcClient, Sales } from 'state/secondaryMarket/shared/referential/referential.model';
import { computeCashAmount } from 'state/secondaryMarket/shared/utils';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';

import type { SecondaryPriceType } from '../productSearch';
import type { CpOrderSettlementType } from './referential/client/client.model';

export type BidAndAsk = { bid: number | undefined; ask: number | undefined };

export function getRelevantPriceDependingOnTradeWay(tradeWay: TradeWay | undefined, prices: BidAndAsk) {
  if (tradeWay === undefined) {
    return undefined;
  }
  return tradeWay === 'ClientSell' ? prices.bid : prices.ask;
}

export function computeCashAmountPerQuantityUnit(
  tradeWay: TradeWay | undefined,
  { bid, ask }: BidAndAsk,
  denomination: number | undefined,
  priceType: SecondaryPriceType,
) {
  const price = getRelevantPriceDependingOnTradeWay(tradeWay, { bid, ask });
  return computeCashAmount(price, denomination, priceType);
}

export function getCobDate(valuationDateIso: string | undefined): string | undefined {
  if (valuationDateIso === undefined) {
    return undefined;
  }
  const valuationDate = parseISO(valuationDateIso);
  const cobDate = addBusinessDays(valuationDate, -1);
  return asIsoDate(cobDate);
}

export function getSalesMarginPlusUpfront(salesMargin: number | undefined, upfront: number | undefined) {
  if (salesMargin === undefined || upfront === undefined) {
    return undefined;
  }
  return salesMargin + upfront;
}

export function fromSettlementTypeKey(settlement: string | undefined): CpOrderSettlementType | undefined {
  if (settlement === undefined) {
    return undefined;
  }
  const [settlementType, marketType] = settlement.split('-', 2);
  return { marketType, settlementType };
}

export function getAmountUnit(priceType: SecondaryPriceType, currency: string): string {
  return priceType === 'Percent' ? '%' : currency;
}

export function isOtcBcsProductKey(bcsProductKey: BcsProductKey | undefined | null): boolean {
  return bcsProductKey?.subType === 'ID_FO';
}

export function getAssociatedSalesDesk(allSales: ReadonlyArray<Sales> | undefined, salesId: string | undefined) {
  return allSales?.find(s => s.id === salesId)?.desk;
}

export function validateClient(item: ISalesCounterpart) {
  const errors = [];
  if (item.eliotCode === undefined) {
    errors.push('Eliot code is missing');
  }
  if (item.id === undefined) {
    errors.push('BDR ID code is missing');
  }
  return errors;
}

export function validateOtcClient(item: OtcClient) {
  const errors = [];
  if (item.eliotCode === undefined) {
    errors.push('Eliot code is missing');
  }
  if (item.id === undefined) {
    errors.push('BDR ID code is missing');
  }
  return errors;
}
